// AGC: Voice ML Admin Dashboard & Tuning Panel
// AGC: Provides monitoring of ML learning progress and admin calibration controls.
// AGC: Dashboard: learning stats, per-user corrections, slot accuracy, voice logs.
// AGC: Tuning: correction config, slot weights, source weights, confirm policy, danger zone.

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../hooks'
import { getBackendUrl } from '../../utils/backendUrl'
import { AdminToggle } from './ui/AdminToggle'

// ─────────────────────────────────────────────────────────
// AGC: Types
// ─────────────────────────────────────────────────────────

interface MLStats {
  period: string
  totalCommands: number
  totalEdits: number
  totalDiscarded: number
  editRate: number
  totalCorrections: number
  uniqueUsers: number
  avgProcessingMs: number
  slotEditCounts: Record<string, number>
  confirmModes: Record<string, number>
  intents: Record<string, number>
}

interface Correction {
  slotType: string
  originalTranscript: string
  correctedFrom: string | null
  correctedTo: string
  timestamp: string
  decayWeight: number
}

interface VoiceLog {
  id: string
  userId: string
  rawTranscript: string
  intentType: string
  confirmMode: string
  editCount: number
  editedSlots: string[]
  processingTimeMs: number
  riskFlags: Array<{ flag: string }>
  wasAutoConfirmed: boolean
  wasDiscarded: boolean
  createdAt: { _seconds: number } | string
}

interface MLConfig {
  slotWeights?: Record<string, Record<string, number>>
  correctionConfig?: {
    decayHalfLifeDays: number
    similarityThreshold: number
    maxCorrections: number
    boostFactor: number
    penaltyFactor: number
  }
  sourceWeights?: Record<string, number>
  confirmPolicy?: Record<string, { instant: number; quick: number; minGap: number; requiresEvidence: boolean }>
  agentChatEnhancedTranscription?: boolean
}

// AGC: Default tuning values (match hardcoded constants in backend)
const DEFAULT_CORRECTION_CONFIG = {
  decayHalfLifeDays: 30,
  similarityThreshold: 0.80,
  maxCorrections: 50,
  boostFactor: 0.15,
  penaltyFactor: 0.10,
}

const DEFAULT_SOURCE_WEIGHTS: Record<string, number> = {
  voice_exact: 1.0,
  voice_phonetic: 0.85,
  voice_fuzzy: 0.8,
  gpt: 0.75,
  template: 0.7,
  last_used: 0.6,
  inferred: 0.4,
  default: 0.2,
}

const DEFAULT_SLOT_WEIGHTS: Record<string, Record<string, number>> = {
  fieldId: { spanExactness: 0.35, sourceWeight: 0.25, recency: 0.10, frequency: 0.15, categoryRelevance: 0.05, availability: 0.05, correctionBias: 0.05 },
  category: { spanExactness: 0.30, sourceWeight: 0.25, recency: 0.05, frequency: 0.25, categoryRelevance: 0.00, availability: 0.05, correctionBias: 0.10 },
  assignee: { spanExactness: 0.40, sourceWeight: 0.25, recency: 0.10, frequency: 0.10, categoryRelevance: 0.05, availability: 0.05, correctionBias: 0.05 },
  dueDate: { spanExactness: 0.40, sourceWeight: 0.30, recency: 0.00, frequency: 0.00, categoryRelevance: 0.00, availability: 0.00, correctionBias: 0.30 },
  dose: { spanExactness: 0.30, sourceWeight: 0.25, recency: 0.05, frequency: 0.05, categoryRelevance: 0.15, availability: 0.10, correctionBias: 0.10 },
  title: { spanExactness: 0.45, sourceWeight: 0.25, recency: 0.00, frequency: 0.00, categoryRelevance: 0.10, availability: 0.00, correctionBias: 0.20 },
  operation: { spanExactness: 0.35, sourceWeight: 0.25, recency: 0.05, frequency: 0.20, categoryRelevance: 0.05, availability: 0.00, correctionBias: 0.10 },
}

const SLOT_LABELS: Record<string, string> = {
  fieldId: 'Field ID',
  category: 'Category',
  assignee: 'Assignee',
  dueDate: 'Due Date',
  dose: 'Dose',
  title: 'Title',
  operation: 'Operation',
}

const FEATURE_LABELS: Record<string, string> = {
  spanExactness: 'Span Exactness',
  sourceWeight: 'Source Weight',
  recency: 'Recency',
  frequency: 'Frequency',
  categoryRelevance: 'Category Relevance',
  availability: 'Availability',
  correctionBias: 'Correction Bias',
}

// AGC: Plain-language help text for each tuning entry (from VOICE_ML_ADMIN_PANEL_GUIDE + VOICE_TUNING_CALIBRATION_GUIDE)
const TUNING_HELP: Record<string, string> = {
  decayHalfLifeDays: 'How long the system "remembers" a correction. At 30 days, a correction is at half strength; at 60 days, about one-quarter. Use a lower value (e.g. 15 days) if old corrections are causing wrong suggestions; use a higher value (e.g. 60 days) if the system seems to forget too quickly.',
  similarityThreshold: 'How similar a new voice command must be to a past correction for that correction to apply. At 80%, the phrase must be quite similar. Lower it (e.g. 70%) if you want corrections to apply to more varied phrases; raise it (e.g. 90%) if the system is applying corrections when the farmer said something different.',
  maxCorrections: 'How many corrections to keep per user. When the limit is reached, the oldest correction is dropped. Increase this if you have power users who correct often; decrease it if you want the system to focus only on recent behavior.',
  boostFactor: 'How strongly to favor a value the farmer previously corrected TO. Higher values make the system learn faster from corrections but can over-correct. If the system is not learning enough, increase slightly; if it flip-flops or over-corrects, decrease.',
  penaltyFactor: 'How strongly to avoid a value the farmer previously corrected AWAY FROM. Keeps wrong suggestions from reappearing. Increase if the same wrong value keeps showing up; decrease if the system is being too cautious.',
  voice_exact: 'When the spoken words match the option name exactly (e.g. "33A" for field 33A). Usually the most reliable — keep this high.',
  voice_phonetic: 'When the match is by sound rather than exact spelling (e.g. accent or pronunciation). Still very reliable.',
  voice_fuzzy: 'When the match is approximate (e.g. typo or similar spelling). Slightly less reliable than exact. Lower this if fuzzy matches often suggest the wrong thing.',
  gpt: 'When the AI (GPT) infers the value from context. Raise if your AI extractions are usually correct; lower if they often suggest wrong values.',
  template: 'Default value from the task template. Raise if your templates are well tuned; lower if template defaults are often wrong.',
  last_used: 'Value the farmer used last time for this type of task. Lower this if "last used" keeps suggesting outdated or wrong values.',
  inferred: 'Value inferred by the system from other fields (e.g. unit from product). Use a moderate value.',
  default: 'System fallback when nothing else matches. Keep low so it only applies when appropriate.',
  spanExactness: 'How much to trust that the spoken text matches the option. Increase when the right option is being said clearly but not chosen; decrease when exact match is too strict.',
  sourceWeight: 'How much to trust the type of match (exact voice, AI, template, etc.). Usually leave as-is unless one source is consistently wrong.',
  recency: 'How much to favor options used recently. Increase if you want "what I used last time" to matter more.',
  frequency: 'How much to favor options the farmer uses most often. Increase when the system keeps suggesting rarely-used options.',
  categoryRelevance: 'How much to favor options that fit the task type (e.g. sprayer for spraying). Increase when wrong categories keep appearing.',
  availability: 'How much to consider whether a machine is active or a product is in stock. Increase when inactive or out-of-stock options keep appearing.',
  correctionBias: 'How much past farmer corrections should influence the ranking. Increase when the system is not learning from corrections; decrease when it over-corrects.',
  clearUserLearning: 'Removes all learned corrections for one user. The system will "forget" everything it learned from that user\'s past corrections. Use when a user\'s corrections are noisy or contradictory, or for testing.',
  resetToDefaults: 'Restores all sliders and tuning values to the original factory settings. Does not delete user corrections — only the tuning numbers. Use when you want to start calibration over.',
}

// AGC: Info icon that opens a modal with help text when clicked
const InfoIcon: React.FC<{
  helpKey: string
  onShowHelp: (text: string) => void
}> = ({ helpKey, onShowHelp }) => {
  const text = TUNING_HELP[helpKey]
  if (!text) return null
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowHelp(text) }}
      title="What is this?"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        border: '1px solid #94a3b8',
        background: '#f1f5f9',
        color: '#64748b',
        fontSize: '11px',
        fontWeight: 700,
        cursor: 'pointer',
        marginLeft: '6px',
        flexShrink: 0,
      }}
    >
      i
    </button>
  )
}

// ─────────────────────────────────────────────────────────
// AGC: Main Component
// ─────────────────────────────────────────────────────────

export const VoiceMLAdmin: React.FC = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tuning'>('dashboard')

  return (
    <div style={{ padding: '0 8px' }}>
      {/* AGC: Tab bar */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('dashboard')}
          style={{
            padding: '10px 20px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'dashboard' ? '2px solid #3b82f6' : '2px solid transparent',
            color: activeTab === 'dashboard' ? '#3b82f6' : '#64748b',
            fontWeight: activeTab === 'dashboard' ? 600 : 400,
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '-2px',
          }}
        >
          {t('admin.voiceML.dashboard') || 'Dashboard'}
        </button>
        <button
          onClick={() => setActiveTab('tuning')}
          style={{
            padding: '10px 20px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'tuning' ? '2px solid #3b82f6' : '2px solid transparent',
            color: activeTab === 'tuning' ? '#3b82f6' : '#64748b',
            fontWeight: activeTab === 'tuning' ? 600 : 400,
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '-2px',
          }}
        >
          {t('admin.voiceML.tuning') || 'Tuning'}
        </button>
      </div>

      {activeTab === 'dashboard' ? <DashboardTab /> : <TuningTab />}
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// AGC: Dashboard Tab
// ─────────────────────────────────────────────────────────

const DashboardTab: React.FC = () => {
  const { t } = useTranslation()
  const [stats, setStats] = useState<MLStats | null>(null)
  const [logs, setLogs] = useState<VoiceLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [corrections, setCorrections] = useState<Correction[]>([])
  const [correctionsLoading, setCorrectionsLoading] = useState(false)
  const [expandedLog, setExpandedLog] = useState<string | null>(null)
  const [logsPage, setLogsPage] = useState(0)

  // AGC: Date range filter state
  const [periodFilter, setPeriodFilter] = useState<'7d' | '30d' | 'all' | 'custom'>('30d')
  const [customDateFrom, setCustomDateFrom] = useState<string>('')
  const [customDateTo, setCustomDateTo] = useState<string>('')

  const backendUrl = getBackendUrl()

  // AGC: Build date params for API calls based on current filter
  const getDateParams = useCallback((): Record<string, string> => {
    if (periodFilter === 'custom' && (customDateFrom || customDateTo)) {
      const params: Record<string, string> = {}
      if (customDateFrom) params.dateFrom = customDateFrom
      if (customDateTo) params.dateTo = customDateTo
      return params
    }
    return { period: periodFilter }
  }, [periodFilter, customDateFrom, customDateTo])

  // AGC: Fetch stats on mount and when filter changes
  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams(getDateParams())
      if (selectedUserId) params.set('userId', selectedUserId)
      const res = await fetch(`${backendUrl}/api/v1/voice/ml/stats?${params}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setStats(data)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [backendUrl, selectedUserId, getDateParams])

  // AGC: Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '20', offset: String(logsPage * 20) })
      if (selectedUserId) params.set('userId', selectedUserId)
      // AGC: Apply date filter to logs too
      const dateParams = getDateParams()
      if (dateParams.dateFrom) params.set('dateFrom', dateParams.dateFrom)
      if (dateParams.dateTo) params.set('dateTo', dateParams.dateTo)
      if (dateParams.period === '7d') {
        const d = new Date(); d.setDate(d.getDate() - 7)
        params.set('dateFrom', d.toISOString().split('T')[0])
      } else if (dateParams.period === '30d') {
        const d = new Date(); d.setDate(d.getDate() - 30)
        params.set('dateFrom', d.toISOString().split('T')[0])
      }
      const res = await fetch(`${backendUrl}/api/v1/voice/ml/logs?${params}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setLogs(data.logs || [])
    } catch (err: any) {
      console.error('AGC: Logs fetch error:', err)
    }
  }, [backendUrl, selectedUserId, logsPage, getDateParams])

  useEffect(() => { fetchStats() }, [fetchStats])
  useEffect(() => { fetchLogs() }, [fetchLogs])

  // AGC: Reset page when filter changes
  useEffect(() => { setLogsPage(0) }, [periodFilter, customDateFrom, customDateTo])

  // AGC: Fetch corrections for a specific user
  const fetchCorrections = useCallback(async (userId: string) => {
    setCorrectionsLoading(true)
    try {
      const res = await fetch(`${backendUrl}/api/v1/voice/ml/corrections/${userId}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setCorrections(data.corrections || [])
    } catch (err: any) {
      console.error('AGC: Corrections fetch error:', err)
    } finally {
      setCorrectionsLoading(false)
    }
  }, [backendUrl])

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading ML stats...</div>
  }

  if (error) {
    return (
      <div style={{ padding: '20px', background: '#fef2f2', borderRadius: '8px', color: '#dc2626' }}>
        Error loading stats: {error}
        <button onClick={fetchStats} style={{ marginLeft: '12px', padding: '4px 12px', borderRadius: '4px', border: '1px solid #dc2626', background: 'white', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    )
  }

  // AGC: Helper to get period display label
  const getPeriodLabel = () => {
    if (periodFilter === '7d') return 'Last 7 days'
    if (periodFilter === '30d') return 'Last 30 days'
    if (periodFilter === 'all') return 'All time'
    if (customDateFrom && customDateTo) return `${customDateFrom} — ${customDateTo}`
    if (customDateFrom) return `From ${customDateFrom}`
    if (customDateTo) return `Until ${customDateTo}`
    return 'Custom range'
  }

  return (
    <div>
      {/* AGC: Date Range Filter Bar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
        marginBottom: '20px', padding: '12px 16px',
        background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0',
      }}>
        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500, marginRight: '4px' }}>Period:</span>
        {(['7d', '30d', 'all', 'custom'] as const).map((p) => (
          <button
            key={p}
            onClick={() => setPeriodFilter(p)}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              borderRadius: '6px',
              border: periodFilter === p ? '1px solid #3b82f6' : '1px solid #d1d5db',
              background: periodFilter === p ? '#eff6ff' : 'white',
              color: periodFilter === p ? '#2563eb' : '#475569',
              fontWeight: periodFilter === p ? 600 : 400,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {p === '7d' ? '7 days' : p === '30d' ? '30 days' : p === 'all' ? 'All' : 'Custom'}
          </button>
        ))}

        {/* AGC: Custom date inputs — only visible when 'custom' is selected */}
        {periodFilter === 'custom' && (
          <>
            <div style={{ width: '1px', height: '24px', background: '#d1d5db', margin: '0 4px' }} />
            <label style={{ fontSize: '12px', color: '#64748b' }}>From:</label>
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              style={{
                padding: '5px 10px', fontSize: '13px', border: '1px solid #d1d5db',
                borderRadius: '6px', background: 'white', color: '#1e293b',
              }}
            />
            <label style={{ fontSize: '12px', color: '#64748b' }}>To:</label>
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              style={{
                padding: '5px 10px', fontSize: '13px', border: '1px solid #d1d5db',
                borderRadius: '6px', background: 'white', color: '#1e293b',
              }}
            />
          </>
        )}
      </div>

      {/* AGC: Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <StatCard
          label={t('admin.voiceML.totalCorrections') || 'Total Commands'}
          value={stats?.totalCommands ?? 0}
          icon="🎤"
          subtitle={getPeriodLabel()}
        />
        <StatCard
          label={t('admin.voiceML.editRate') || 'Edit Rate'}
          value={`${stats?.editRate ?? 0}%`}
          icon="✏️"
          subtitle={`${stats?.totalEdits ?? 0} edits`}
          color={
            (stats?.editRate ?? 0) > 30 ? '#dc2626' :
            (stats?.editRate ?? 0) > 15 ? '#f59e0b' : '#22c55e'
          }
        />
        <StatCard
          label={t('admin.voiceML.accuracy') || 'Accuracy'}
          value={`${100 - (stats?.editRate ?? 0)}%`}
          icon="🎯"
          subtitle="No-edit rate"
          color={(100 - (stats?.editRate ?? 0)) >= 85 ? '#22c55e' : '#f59e0b'}
        />
        <StatCard
          label="Avg Processing"
          value={`${stats?.avgProcessingMs ?? 0}ms`}
          icon="⚡"
          subtitle={`${stats?.uniqueUsers ?? 0} users`}
        />
      </div>

      {/* AGC: Slot Edit Rates Table */}
      {stats?.slotEditCounts && Object.keys(stats.slotEditCounts).length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
            Slot Accuracy
          </h3>
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={thStyle}>Slot</th>
                  <th style={thStyle}>Edits</th>
                  <th style={thStyle}>Edit Rate</th>
                  <th style={thStyle}>Bar</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.slotEditCounts)
                  .sort(([, a], [, b]) => b - a)
                  .map(([slot, count]) => {
                    const rate = stats.totalCommands > 0 ? Math.round((count / stats.totalCommands) * 100) : 0
                    return (
                      <tr key={slot} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={tdStyle}>{SLOT_LABELS[slot] || slot}</td>
                        <td style={tdStyle}>{count}</td>
                        <td style={tdStyle}>{rate}%</td>
                        <td style={tdStyle}>
                          <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(rate, 100)}%`, height: '100%', background: rate > 30 ? '#dc2626' : rate > 15 ? '#f59e0b' : '#22c55e', borderRadius: '4px' }} />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AGC: Confirm Mode Distribution */}
      {stats?.confirmModes && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
            Confirm Mode Distribution
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {Object.entries(stats.confirmModes).map(([mode, count]) => (
              <div key={mode} style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '12px 16px', minWidth: '140px' }}>
                <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>{mode.replace('_', ' ')}</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AGC: User Corrections Viewer */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
          {t('admin.voiceML.corrections') || 'User Corrections'}
        </h3>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="User ID..."
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '300px' }}
          />
          <button
            onClick={() => { if (selectedUserId) fetchCorrections(selectedUserId) }}
            disabled={!selectedUserId}
            style={{ padding: '8px 16px', background: selectedUserId ? '#3b82f6' : '#94a3b8', color: 'white', border: 'none', borderRadius: '6px', cursor: selectedUserId ? 'pointer' : 'not-allowed', fontSize: '14px' }}
          >
            Load Corrections
          </button>
          <button onClick={fetchStats} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
            Refresh Stats
          </button>
        </div>

        {correctionsLoading && <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Loading corrections...</div>}

        {!correctionsLoading && corrections.length > 0 && (
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={thStyle}>Slot</th>
                  <th style={thStyle}>From</th>
                  <th style={thStyle}>To</th>
                  <th style={thStyle}>Transcript</th>
                  <th style={thStyle}>Decay</th>
                  <th style={thStyle}>Date</th>
                </tr>
              </thead>
              <tbody>
                {corrections.map((c, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={tdStyle}>
                      <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                        {c.slotType}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: '#dc2626' }}>{String(c.correctedFrom ?? '—')}</td>
                    <td style={{ ...tdStyle, color: '#22c55e', fontWeight: 600 }}>{String(c.correctedTo)}</td>
                    <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.originalTranscript || '—'}
                    </td>
                    <td style={tdStyle}>
                      <DecayBar weight={c.decayWeight} />
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px', color: '#64748b' }}>
                      {c.timestamp ? new Date(c.timestamp).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* AGC: Recent Voice Logs */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
          Recent Voice Logs
        </h3>
        {logs.length === 0 ? (
          <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', color: '#64748b' }}>
            No voice logs found
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Transcript</th>
                  <th style={thStyle}>Intent</th>
                  <th style={thStyle}>Mode</th>
                  <th style={thStyle}>Edits</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const ts = log.createdAt
                    ? typeof log.createdAt === 'string'
                      ? new Date(log.createdAt)
                      : new Date((log.createdAt as any)._seconds * 1000)
                    : null
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        style={{ borderTop: '1px solid #e2e8f0', cursor: 'pointer', background: expandedLog === log.id ? '#f8fafc' : 'white' }}
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      >
                        <td style={{ ...tdStyle, fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {ts ? ts.toLocaleString() : '—'}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.userId?.slice(0, 8)}...
                        </td>
                        <td style={{ ...tdStyle, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.rawTranscript || '—'}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            {log.intentType || '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            background: log.confirmMode === 'instant' ? '#dcfce7' : log.confirmMode === 'quick_confirm' ? '#fef9c3' : '#f1f5f9',
                            color: log.confirmMode === 'instant' ? '#166534' : log.confirmMode === 'quick_confirm' ? '#854d0e' : '#475569',
                            padding: '2px 8px', borderRadius: '4px', fontSize: '12px',
                          }}>
                            {log.confirmMode || 'modal'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {(log.editCount || 0) > 0 ? (
                            <span style={{ color: '#dc2626', fontWeight: 600 }}>{log.editCount}</span>
                          ) : (
                            <span style={{ color: '#22c55e' }}>0</span>
                          )}
                        </td>
                      </tr>
                      {expandedLog === log.id && (
                        <tr>
                          <td colSpan={6} style={{ padding: '12px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                              <div><strong>Full Transcript:</strong> {log.rawTranscript}</div>
                              <div><strong>Processing Time:</strong> {log.processingTimeMs}ms</div>
                              <div><strong>Edited Slots:</strong> {(log.editedSlots || []).join(', ') || 'none'}</div>
                              <div><strong>Risk Flags:</strong> {(log.riskFlags || []).map(rf => rf.flag).join(', ') || 'none'}</div>
                              <div><strong>Discarded:</strong> {log.wasDiscarded ? 'Yes' : 'No'}</div>
                              <div><strong>User ID:</strong> {log.userId}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
            <div style={{ padding: '12px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
              <button
                onClick={() => setLogsPage(Math.max(0, logsPage - 1))}
                disabled={logsPage === 0}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: logsPage === 0 ? 'not-allowed' : 'pointer' }}
              >
                Previous
              </button>
              <span style={{ padding: '6px 12px', color: '#64748b' }}>Page {logsPage + 1}</span>
              <button
                onClick={() => setLogsPage(logsPage + 1)}
                disabled={logs.length < 20}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: logs.length < 20 ? 'not-allowed' : 'pointer' }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// AGC: Tuning Tab
// ─────────────────────────────────────────────────────────

const TuningTab: React.FC = () => {
  const { t } = useTranslation()
  const [config, setConfig] = useState<MLConfig>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [isDefault, setIsDefault] = useState(true)
  const [expandedSlot, setExpandedSlot] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState<string | null>(null)
  const [resetUserId, setResetUserId] = useState('')
  const [helpModalContent, setHelpModalContent] = useState<string | null>(null)
  const [agentEnhanced, setAgentEnhanced] = useState(false)

  const backendUrl = getBackendUrl()

  const showHelp = useCallback((text: string) => setHelpModalContent(text), [])

  // AGC: Fetch current config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/v1/voice/ml/config`)
        if (res.ok) {
          const data = await res.json()
          setConfig(data.config || {})
          setIsDefault(data.isDefault)
          setAgentEnhanced(!!(data.config?.agentChatEnhancedTranscription))
        }
      } catch (err: any) {
        console.error('AGC: Config fetch error:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchConfig()
  }, [backendUrl])

  // AGC: Save config to backend
  const saveConfig = useCallback(async (updates: Partial<MLConfig>) => {
    setSaving(true)
    setSaveMessage(null)
    try {
      const res = await fetch(`${backendUrl}/api/v1/voice/ml/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      })
      if (res.ok) {
        setConfig(prev => ({ ...prev, ...updates }))
        setSaveMessage('Saved successfully!')
        setIsDefault(false)
        setTimeout(() => setSaveMessage(null), 3000)
      } else {
        setSaveMessage('Error saving config')
      }
    } catch (err: any) {
      setSaveMessage(`Error: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }, [backendUrl])

  // AGC: Reset all corrections for a user
  const resetUserCorrections = useCallback(async (userId: string) => {
    try {
      const res = await fetch(`${backendUrl}/api/v1/voice/ml/corrections/${userId}`, { method: 'DELETE' })
      if (res.ok) {
        setSaveMessage(`Corrections cleared for ${userId}`)
        setConfirmReset(null)
        setResetUserId('')
        setTimeout(() => setSaveMessage(null), 3000)
      }
    } catch (err: any) {
      setSaveMessage(`Error: ${err.message}`)
    }
  }, [backendUrl])

  // AGC: Reset config to factory defaults
  const resetToDefaults = useCallback(async () => {
    await saveConfig({
      correctionConfig: DEFAULT_CORRECTION_CONFIG,
      sourceWeights: DEFAULT_SOURCE_WEIGHTS,
      slotWeights: DEFAULT_SLOT_WEIGHTS,
    })
    setConfirmReset(null)
  }, [saveConfig])

  // AGC: Get current value with fallback to default
  const getCorrectionVal = (key: keyof typeof DEFAULT_CORRECTION_CONFIG) => {
    return config.correctionConfig?.[key] ?? DEFAULT_CORRECTION_CONFIG[key]
  }

  const getSourceWeight = (source: string) => {
    return config.sourceWeights?.[source] ?? DEFAULT_SOURCE_WEIGHTS[source] ?? 0.5
  }

  const getSlotWeight = (slot: string, feature: string) => {
    return config.slotWeights?.[slot]?.[feature] ?? DEFAULT_SLOT_WEIGHTS[slot]?.[feature] ?? 0.1
  }

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading config...</div>
  }

  return (
    <div>
      {/* AGC: Agent-Chat Enhanced Transcription Toggle */}
      <div className={`mb-6 p-4 rounded-xl border-2 transition-colors ${
        agentEnhanced
          ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700'
          : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <h4 className={`text-sm font-semibold mb-1 ${
              agentEnhanced ? 'text-emerald-800 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'
            }`}>
              {t('admin.voiceML.agentEnhancedTitle') || 'Voice Recognition - Farm Agent'}
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('admin.voiceML.agentEnhancedDesc') || 'When active, uses SOTA transcription with calibration profile in Farm Agent'}
            </p>
          </div>
          <AdminToggle
            checked={agentEnhanced}
            onChange={async (checked: boolean) => {
              await saveConfig({ agentChatEnhancedTranscription: checked })
              setAgentEnhanced(checked)
            }}
          />
        </div>
        {agentEnhanced && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
            {t('admin.voiceML.agentEnhancedWarning') || 'Active: uses gpt-4o-transcribe for calibrated users'}
          </p>
        )}
      </div>

      {/* AGC: Save status */}
      {saveMessage && (
        <div style={{
          padding: '10px 16px',
          marginBottom: '16px',
          borderRadius: '6px',
          background: saveMessage.startsWith('Error') ? '#fef2f2' : '#f0fdf4',
          color: saveMessage.startsWith('Error') ? '#dc2626' : '#166534',
          fontSize: '14px',
        }}>
          {saveMessage}
        </div>
      )}

      {isDefault && (
        <div style={{ padding: '10px 16px', marginBottom: '16px', borderRadius: '6px', background: '#fef9c3', color: '#854d0e', fontSize: '13px' }}>
          Using default values. Changes will be saved to Firestore and take effect immediately (no deploy needed).
        </div>
      )}

      {/* AGC: Correction Learning Settings */}
      <SectionCard title={t('admin.voiceML.corrections') || 'Correction Learning Settings'} icon="🧠">
        <SliderRow
          label={t('admin.voiceML.decayHalfLife') || 'Decay Half-Life (days)'}
          tooltip="How fast should old corrections fade? Lower = faster decay."
          value={getCorrectionVal('decayHalfLifeDays')}
          min={1} max={90} step={1}
          onChange={(v) => saveConfig({ correctionConfig: { ...config.correctionConfig, ...DEFAULT_CORRECTION_CONFIG, ...config.correctionConfig, decayHalfLifeDays: v } })}
          disabled={saving}
          helpKey="decayHalfLifeDays"
          onShowHelp={showHelp}
        />
        <SliderRow
          label={t('admin.voiceML.similarityThreshold') || 'Similarity Threshold'}
          tooltip="How similar must a new transcript be for a correction to apply?"
          value={getCorrectionVal('similarityThreshold')}
          min={0.5} max={1.0} step={0.05}
          format={(v) => `${Math.round(v * 100)}%`}
          onChange={(v) => saveConfig({ correctionConfig: { ...DEFAULT_CORRECTION_CONFIG, ...config.correctionConfig, similarityThreshold: v } })}
          disabled={saving}
          helpKey="similarityThreshold"
          onShowHelp={showHelp}
        />
        <SliderRow
          label={t('admin.voiceML.maxCorrections') || 'Max Corrections'}
          tooltip="How many corrections to remember per user?"
          value={getCorrectionVal('maxCorrections')}
          min={10} max={200} step={10}
          onChange={(v) => saveConfig({ correctionConfig: { ...DEFAULT_CORRECTION_CONFIG, ...config.correctionConfig, maxCorrections: v } })}
          disabled={saving}
          helpKey="maxCorrections"
          onShowHelp={showHelp}
        />
        <SliderRow
          label={t('admin.voiceML.boostFactor') || 'Boost Factor'}
          tooltip="How much to boost corrected-to values (0.05 = subtle, 0.30 = strong)"
          value={getCorrectionVal('boostFactor')}
          min={0.05} max={0.30} step={0.01}
          format={(v) => v.toFixed(2)}
          onChange={(v) => saveConfig({ correctionConfig: { ...DEFAULT_CORRECTION_CONFIG, ...config.correctionConfig, boostFactor: v } })}
          disabled={saving}
          helpKey="boostFactor"
          onShowHelp={showHelp}
        />
        <SliderRow
          label={t('admin.voiceML.penaltyFactor') || 'Penalty Factor'}
          tooltip="How much to penalize corrected-from values"
          value={getCorrectionVal('penaltyFactor')}
          min={0.05} max={0.20} step={0.01}
          format={(v) => v.toFixed(2)}
          onChange={(v) => saveConfig({ correctionConfig: { ...DEFAULT_CORRECTION_CONFIG, ...config.correctionConfig, penaltyFactor: v } })}
          disabled={saving}
          helpKey="penaltyFactor"
          onShowHelp={showHelp}
        />
      </SectionCard>

      {/* AGC: Source Trust Weights */}
      <SectionCard title={t('admin.voiceML.sourceWeights') || 'Source Trust Weights'} icon="🔗">
        {Object.entries(DEFAULT_SOURCE_WEIGHTS).map(([source, defaultVal]) => (
          <SliderRow
            key={source}
            label={source.replace('_', ' ')}
            tooltip={`Trust weight for ${source} matches (default: ${defaultVal})`}
            value={getSourceWeight(source)}
            min={0} max={1} step={0.05}
            format={(v) => v.toFixed(2)}
            onChange={(v) => saveConfig({ sourceWeights: { ...DEFAULT_SOURCE_WEIGHTS, ...config.sourceWeights, [source]: v } })}
            disabled={saving}
            helpKey={source}
            onShowHelp={showHelp}
          />
        ))}
      </SectionCard>

      {/* AGC: Slot Feature Weights */}
      <SectionCard title={t('admin.voiceML.slotWeights') || 'Slot Feature Weights'} icon="⚖️">
        {Object.entries(DEFAULT_SLOT_WEIGHTS).map(([slot, defaultWeights]) => {
          const currentWeights = Object.keys(defaultWeights).reduce((acc, feat) => {
            acc[feat] = getSlotWeight(slot, feat)
            return acc
          }, {} as Record<string, number>)
          const total = Object.values(currentWeights).reduce((s, v) => s + v, 0)

          return (
            <div key={slot} style={{ marginBottom: '8px' }}>
              <div
                onClick={() => setExpandedSlot(expandedSlot === slot ? null : slot)}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', background: '#f8fafc', borderRadius: '6px', cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                }}
              >
                <span style={{ fontWeight: 500, fontSize: '14px' }}>
                  {expandedSlot === slot ? '▼' : '▶'} {SLOT_LABELS[slot] || slot}
                </span>
                <span style={{
                  fontSize: '12px',
                  color: Math.abs(total - 1.0) < 0.01 ? '#22c55e' : '#dc2626',
                  fontWeight: 600,
                }}>
                  Sum: {total.toFixed(2)}
                </span>
              </div>
              {expandedSlot === slot && (
                <div style={{ padding: '12px', background: 'white', borderRadius: '0 0 6px 6px', border: '1px solid #e2e8f0', borderTop: 'none' }}>
                  {Object.entries(defaultWeights).map(([feat, defVal]) => (
                    <SliderRow
                      key={feat}
                      label={FEATURE_LABELS[feat] || feat}
                      tooltip={`Default: ${defVal}`}
                      value={getSlotWeight(slot, feat)}
                      min={0} max={0.5} step={0.01}
                      format={(v) => v.toFixed(2)}
                      onChange={(v) => {
                        const updated = { ...DEFAULT_SLOT_WEIGHTS[slot], ...config.slotWeights?.[slot], [feat]: v }
                        saveConfig({ slotWeights: { ...config.slotWeights, [slot]: updated } })
                      }}
                      disabled={saving}
                      helpKey={feat}
                      onShowHelp={showHelp}
                    />
                  ))}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      onClick={() => saveConfig({ slotWeights: { ...config.slotWeights, [slot]: defaultWeights } })}
                      style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                    >
                      Reset to defaults
                    </button>
                    <button
                      onClick={() => {
                        // AGC: Auto-normalize weights to sum to 1.0
                        const curr = { ...DEFAULT_SLOT_WEIGHTS[slot], ...config.slotWeights?.[slot] }
                        const sum = Object.values(curr).reduce((s, v) => s + v, 0)
                        if (sum > 0) {
                          const normalized = Object.fromEntries(Object.entries(curr).map(([k, v]) => [k, parseFloat((v / sum).toFixed(3))]))
                          saveConfig({ slotWeights: { ...config.slotWeights, [slot]: normalized } })
                        }
                      }}
                      style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #3b82f6', borderRadius: '4px', background: '#eff6ff', color: '#3b82f6', cursor: 'pointer' }}
                    >
                      Auto-normalize
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </SectionCard>

      {/* AGC: Danger Zone */}
      <SectionCard title="Danger Zone" icon="⚠️" borderColor="#fecaca">
        {/* AGC: Clear user corrections */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
            Clear User Learning
            <InfoIcon helpKey="clearUserLearning" onShowHelp={showHelp} />
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="User ID..."
              value={resetUserId}
              onChange={(e) => setResetUserId(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '250px' }}
            />
            <button
              onClick={() => setConfirmReset('user')}
              disabled={!resetUserId}
              style={{ padding: '8px 16px', background: resetUserId ? '#dc2626' : '#94a3b8', color: 'white', border: 'none', borderRadius: '6px', cursor: resetUserId ? 'pointer' : 'not-allowed', fontSize: '14px' }}
            >
              {t('admin.voiceML.resetCorrections') || 'Reset Corrections'}
            </button>
          </div>
        </div>

        {/* AGC: Factory reset */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
            {t('admin.voiceML.resetDefaults') || 'Reset to Factory Defaults'}
            <InfoIcon helpKey="resetToDefaults" onShowHelp={showHelp} />
          </div>
          <button
            onClick={() => setConfirmReset('factory')}
            style={{ padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
          >
            Reset All Tuning
          </button>
        </div>
      </SectionCard>

      {/* AGC: Help modal — shown when user clicks (i) on a tuning entry */}
      {helpModalContent && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9998,
          }}
          onClick={() => setHelpModalContent(null)}
        >
          <div
            style={{
              background: 'white', borderRadius: '12px', padding: '20px 24px', maxWidth: '420px', width: '90%', maxHeight: '80vh', overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ color: '#334155', fontSize: '14px', lineHeight: 1.6, margin: '0 0 16px' }}>{helpModalContent}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setHelpModalContent(null)}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#f8fafc', cursor: 'pointer', fontSize: '14px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Confirmation dialogs */}
      {confirmReset && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999,
        }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', color: '#dc2626' }}>Confirm Reset</h3>
            <p style={{ color: '#475569', fontSize: '14px', marginBottom: '20px' }}>
              {confirmReset === 'user'
                ? `This will clear all corrections for user "${resetUserId}". The system will forget everything it learned from this user's corrections.`
                : 'This will reset all tuning parameters to factory defaults. Current custom settings will be overwritten.'}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmReset(null)}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => confirmReset === 'user' ? resetUserCorrections(resetUserId) : resetToDefaults()}
                style={{ padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Yes, Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// AGC: Reusable Sub-Components
// ─────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string | number; icon: string; subtitle: string; color?: string }> = ({ label, value, icon, subtitle, color }) => (
  <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px', display: 'flex', flexDirection: 'column' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
      <span style={{ fontSize: '13px', color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: '20px' }}>{icon}</span>
    </div>
    <div style={{ fontSize: '28px', fontWeight: 700, color: color || '#1e293b' }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{subtitle}</div>
  </div>
)

const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode; borderColor?: string }> = ({ title, icon, children, borderColor }) => (
  <div style={{ marginBottom: '24px', background: 'white', borderRadius: '12px', border: `1px solid ${borderColor || '#e2e8f0'}`, overflow: 'hidden' }}>
    <div style={{ padding: '16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>{icon} {title}</h3>
    </div>
    <div style={{ padding: '16px' }}>{children}</div>
  </div>
)

const SliderRow: React.FC<{
  label: string
  tooltip: string
  value: number
  min: number
  max: number
  step: number
  format?: (v: number) => string
  onChange: (v: number) => void
  disabled?: boolean
  helpKey?: string
  onShowHelp?: (text: string) => void
}> = ({ label, tooltip, value, min, max, step, format, onChange, disabled, helpKey, onShowHelp }) => (
  <div style={{ marginBottom: '12px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
      <span style={{ display: 'flex', alignItems: 'center', fontSize: '13px', color: '#475569' }}>
        <label title={tooltip}>{label}</label>
        {helpKey && onShowHelp && <InfoIcon helpKey={helpKey} onShowHelp={onShowHelp} />}
      </span>
      <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', minWidth: '60px', textAlign: 'right' }}>
        {format ? format(value) : value}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      disabled={disabled}
      style={{ width: '100%', accentColor: '#3b82f6' }}
    />
  </div>
)

const DecayBar: React.FC<{ weight: number }> = ({ weight }) => {
  const pct = Math.round(weight * 100)
  const color = pct > 70 ? '#22c55e' : pct > 40 ? '#f59e0b' : '#dc2626'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ width: '60px', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px' }} />
      </div>
      <span style={{ fontSize: '11px', color: '#64748b' }}>{pct}%</span>
    </div>
  )
}

// AGC: Table styles
const thStyle: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }
const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: '13px', color: '#1e293b' }

export default VoiceMLAdmin
